import {
    createFinancialAidActionErrorReport,
    readFinancialAidActionErrorReport,
    type FinancialAidActionErrorReport,
} from '@triedge/financial-aid-domain/value-objects';

export {
    createFinancialAidActionErrorReport,
    readFinancialAidActionErrorMessage,
    readFinancialAidActionErrorReport,
    type FinancialAidActionErrorReport,
} from '@triedge/financial-aid-domain/value-objects';

/**
 * Parent-facing copy shown when the cycle-scoped fee gate blocks a
 * financial aid submission (INT-636). The raw backend message stays
 * available inside the collapsed technical-details disclosure.
 */
export const PARENT_FINANCIAL_AID_FEE_BLOCKED_SUBMIT_MESSAGE =
    'Before submitting financial aid, please complete the registration fee or contact the school office.';

export const PARENT_FINANCIAL_AID_FEE_BLOCKED_SUBMIT_CODE = 'REGISTRATION_FEE_REQUIRED';

// Stable substring shared by both backend fee-gate messages
// ("...before submitting financial aid application" from the submit path and
// "...before accessing the financial aid form..." from the access path).
const REGISTRATION_FEE_GATE_MESSAGE_PATTERN = /registration fee must be paid/i;

const readCandidateErrorText = (error: unknown, depth = 0): string | null => {
    if (depth > 3 || error == null) return null;
    if (typeof error === 'string') return error.trim() || null;
    if (error instanceof Error) return error.message.trim() || null;
    if (typeof error !== 'object') return null;
    const record = error as Record<string, unknown>;
    for (const key of ['message', 'errorMessage'] as const) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    for (const key of ['data', 'error', 'cause'] as const) {
        const nested = readCandidateErrorText(record[key], depth + 1);
        if (nested) return nested;
    }
    return null;
};

export const isParentFinancialAidFeeBlockedSubmitError = (
    error: unknown,
    report?: Pick<FinancialAidActionErrorReport, 'userMessage'> | null,
): boolean => [readCandidateErrorText(error), report?.userMessage ?? null]
    .some((text) => typeof text === 'string' && REGISTRATION_FEE_GATE_MESSAGE_PATTERN.test(text));

/**
 * Build the submit-failure report for the parent form. Fee-gate failures are
 * remapped to friendly parent-facing copy while preserving the raw backend
 * message inside the technical report; all other errors pass through
 * unchanged.
 */
export const readParentFinancialAidSubmitErrorReport = (
    error: unknown,
    fallback: string,
): FinancialAidActionErrorReport => {
    const report = readFinancialAidActionErrorReport(error, fallback);
    if (!isParentFinancialAidFeeBlockedSubmitError(error, report)) return report;
    const backendMessage = readCandidateErrorText(error) ?? report.userMessage;
    return createFinancialAidActionErrorReport(
        PARENT_FINANCIAL_AID_FEE_BLOCKED_SUBMIT_MESSAGE,
        [`Backend message: ${backendMessage}`, report.technicalMessage].filter(Boolean).join('\n'),
        report.code ?? PARENT_FINANCIAL_AID_FEE_BLOCKED_SUBMIT_CODE,
    );
};

/**
 * Visible headline for the submit error banner. Fee-gate copy is already a
 * complete parent-facing sentence, so it must not be prefixed with the
 * technical-sounding "Submission failed:" lead-in.
 */
export const formatParentFinancialAidSubmitErrorHeadline = (
    report: Pick<FinancialAidActionErrorReport, 'userMessage'>,
): string => (
    report.userMessage === PARENT_FINANCIAL_AID_FEE_BLOCKED_SUBMIT_MESSAGE
        ? report.userMessage
        : `Submission failed: ${report.userMessage}`
);
